import { useState } from 'react';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconReplace } from '@components/icons/replace';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { ModifiedTravellerJourney } from 'api/traveller-journeys/types';

export const usePublishAddTravellerActions = () =>
  useModal(PublishAddActionsOnTravellers, { variant: 'REGULAR' });

type PublishProps = {
  travellers: ModifiedTravellerJourney[];
  onUpdate: (ids: string[]) => void;
  cancelButtonText?: string;
  onCancel?: () => void;
};

export const PublishAddActionsOnTravellers = (props: PublishProps) => {
  const { travellers, onUpdate, onCancel, cancelButtonText } = props;
  const { t } = useScopedTranslate(locale);
  const [checkedTravellers, setCheckedTravellers] = useState<string[]>([]);

  const numTravellers = travellers.length;
  const hasSelectedTravellers = checkedTravellers.length > 0;

  return (
    <Modal>
      <Modal.SimpleHeader
        title={t('title', { numTravellers: numTravellers.toString() })}
        skipCloseButton
      />
      <Modal.Content>
        <Stack space={6}>
          <Text text={t('topDescription', { numTravellers: numTravellers.toString() })} />
          <Stack space={2}>
            {numTravellers > 1 && (
              <Stack space={2} orientation="horizontal">
                <Checkbox
                  value={checkedTravellers.length === numTravellers}
                  onChange={() => {
                    if (checkedTravellers.length === numTravellers) {
                      setCheckedTravellers([]);
                    } else {
                      setCheckedTravellers(travellers.map((traveller) => traveller.shortId));
                    }
                  }}
                  label={t('checkbox') + t('selectAll')}
                  labelVisible={false}
                />
                <Text text={t('selectAll')} font="bold14" />
              </Stack>
            )}
            {travellers.map((traveller) => {
              const isChecked = checkedTravellers.includes(traveller.shortId);
              return (
                <Stack space={2} orientation="horizontal" key={traveller.shortId}>
                  <Checkbox
                    value={isChecked}
                    onChange={() => {
                      if (isChecked) {
                        setCheckedTravellers(
                          checkedTravellers.filter((id) => id !== traveller.shortId)
                        );
                      } else {
                        setCheckedTravellers([...checkedTravellers, traveller.shortId]);
                      }
                    }}
                    label={t('checkbox') + traveller.name}
                    labelVisible={false}
                  />
                  <Text text={traveller.name} />
                </Stack>
              );
            })}
          </Stack>
          {hasSelectedTravellers && <Text text={t('bottomDescription')} />}
          <Stack orientation="horizontal" space={2} justify="flex-end">
            <Button
              variant="secondary"
              text={cancelButtonText ?? t('core.cancel')}
              onClick={() => {
                onCancel?.();
                hideModal();
              }}
            />
            <Button
              variant="primary-blue"
              text={t('update')}
              onClick={() => onUpdate(checkedTravellers)}
              iconLeft={<IconReplace />}
              disabled={!hasSelectedTravellers}
            />
          </Stack>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Update modified traveller(s) • {numTravellers} ?',
    topDescription:
      'You have {numTravellers} travellers with manual changes applied to their journeys, would you like to update those travellers journeys? ',
    bottomDescription:
      'Selected travellers journeys modifications will be overwritten with the new updated version and manual changes will be lost',
    update: 'Update travellers',
    checkbox: 'Checkbox for: ',
    selectAll: 'Select all',
  },
};
